/**
 * The client's photography falls into three distinct shoots. Treating them as
 * named editorial stories gives the lookbook a spine, rather than dumping 41
 * frames into one grid.
 *
 * `w` marks landscape frames so the masonry can span them across two columns.
 */

const L = (name, alt, w = false) => ({
  src: `/assets/lookbook/${name}.webp`,
  thumb: `/assets/lookbook/${name}-sm.webp`,
  alt,
  wide: w,
});

export const stories = [
  {
    slug: 'sapphire',
    title: 'Sapphire',
    eyebrow: 'Story One',
    subtitle: 'Polki and ruby beads against royal blue',
    text: 'A full bridal suite photographed on a sapphire-blue lehenga — layered choker and long haar, chandbali, nath, maang tikka, kada and cocktail rings, all closed-set polki strung with rose-pink beads.',
    cover: L('sapphire-01', 'Bride in a royal blue lehenga wearing a layered polki and ruby-bead bridal suite'),
    photos: [
      L('sapphire-01', 'Seated bride in royal blue wearing a polki choker, long haar and nath'),
      L('sapphire-w-01', 'Close frame of a polki choker, chandbali and nath with rose-pink beads', true),
      L('sapphire-03', 'Bride looking down, polki choker and long haar over a blue organza dupatta'),
      L('sapphire-05', 'Polki bridal suite with maang tikka and nose chain in royal blue'),
      L('sapphire-14', 'Hands raised to the face, showing chandbali earrings and polki bangles'),
      L('sapphire-w-03', 'Detail of a pink-bead polki necklace and matching kada', true),
      L('sapphire-06', 'Full-length bridal portrait in an embroidered royal blue lehenga'),
      L('sapphire-09', 'Bride lifting a blue organza dupatta, polki choker visible'),
      L('sapphire-11', 'Bride with eyes closed wearing a polki choker and drop earrings'),
      L('sapphire-w-02', 'Seated frame showing a graduated pink-bead necklace', true),
      L('sapphire-13', 'Polki cocktail ring and bangles held against blue embroidery'),
      L('sapphire-04', 'Three-quarter portrait with polki choker, tikka and nath'),
      L('sapphire-10', 'Bride standing in a royal blue lehenga with a polki bridal set'),
      L('sapphire-w-04', 'Bride turning away, long haar falling over the shoulder', true),
      L('sapphire-15', 'Bride in blue organza with a polki and pink-bead necklace'),
      L('sapphire-02', 'Seated portrait, polki choker and stacked bangles'),
      L('sapphire-07', 'Standing portrait with polki choker and cocktail rings'),
      L('sapphire-12', 'Bride with dupatta draped, showing a layered polki suite'),
      L('sapphire-w-05', 'Bride resting a hand at the collar, polki necklace in focus', true),
      L('sapphire-16', 'Bride in a sequinned blue lehenga with a polki bridal necklace'),
      L('sapphire-08', 'Standing bridal portrait with maang tikka and layered necklaces'),
    ],
  },
  {
    slug: 'ivory',
    title: 'Ivory',
    eyebrow: 'Story Two',
    subtitle: 'Emerald and pearl on chikankari white',
    text: 'The same craft read quietly. Emerald-and-pearl polki over ivory chikankari — a close choker with a long haar beneath it, stacked bangles, and a kalgi pinned to the safa for the groom.',
    cover: L('ivory-08', 'Model in ivory chikankari wearing an emerald and pearl polki choker and long haar'),
    photos: [
      L('ivory-08', 'Ivory chikankari lehenga with an emerald and pearl polki choker'),
      L('ivory-04', 'Seated portrait, emerald polki choker and long haar on ivory'),
      L('ivory-13', 'Ivory safa with a jewelled kalgi and an emerald polki necklace', false),
      L('ivory-07', 'Standing portrait in ivory with a layered emerald polki suite'),
      L('ivory-w-01', 'Close frame of a jewelled kalgi on an ivory safa', true),
      L('ivory-02', 'Emerald and pearl choker with matched drop earrings on ivory'),
      L('ivory-11', 'Model with eyes closed wearing an emerald polki choker'),
      L('ivory-06', 'Seated ivory portrait with stacked polki bangles'),
      L('ivory-14', 'Ivory safa and kalgi, hand resting at the chin'),
      L('ivory-05', 'Emerald polki long haar over ivory chikankari'),
      L('ivory-09', 'Standing portrait in ivory with a polki choker and bangles'),
      L('ivory-03', 'Three-quarter ivory portrait with maang tikka and choker'),
      L('ivory-10', 'Ivory cape drape with an emerald and pearl polki set'),
      L('ivory-12', 'Seated ivory portrait with a fine emerald polki choker'),
      L('ivory-15', 'Ivory portrait, hand at the collar showing a polki necklace'),
      L('ivory-01', 'Model in ivory with an emerald polki choker and long haar'),
    ],
  },
  {
    slug: 'noir',
    title: 'Noir',
    eyebrow: 'Story Three',
    subtitle: 'One pendant, nothing else',
    text: 'Polki taken out of the wedding entirely — a single medallion on black, worn with a stacked kada. Proof that the same stones read as modern when the styling gets out of the way.',
    cover: L('noir-02', 'Model in black wearing a single polki medallion pendant'),
    photos: [
      L('noir-02', 'Polki medallion pendant on a black high-neck top'),
      L('noir-03', 'Profile frame showing a polki drop earring and stacked kada'),
      L('noir-01', 'Seated portrait in black with a polki medallion necklace'),
      L('noir-04', 'Model in black resting a hand at the brow, polki pendant visible'),
    ],
  },
];

/** Flat list for the lightbox and for "all" filtering. */
export const allPhotos = stories.flatMap((s) =>
  s.photos.map((p) => ({ ...p, story: s.title, storySlug: s.slug }))
);
